import React, { useState, useEffect } from 'react';
import { Package, Plus, Trash2, Edit2, Droplet, DollarSign, Archive, PlusSquare, History } from 'lucide-react';

const LubeConfig = () => {
  const [lubes, setLubes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newLube, setNewLube] = useState({ name: '', pack_size: '', rate: '', current_stock: '' });
  const [editingLube, setEditingLube] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [addingStockLube, setAddingStockLube] = useState(null);
  const [newReceipt, setNewReceipt] = useState({ quantity: '', invoice_number: '', business_date: new Date().toISOString().split('T')[0] });
  
  const isAdmin = localStorage.getItem('role') === 'admin';

  useEffect(() => {
    fetchLubes();
  }, []);

  const fetchLubes = async () => {
    setIsLoading(true);
    try {
      const res = await fetch('/api/lubes', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setLubes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setLubes([]);
    }
    setIsLoading(false);
  };

  const handleAddLube = async (e) => {
    e.preventDefault();
    try {
      await fetch('/api/lubes', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newLube)
      });
      setNewLube({ name: '', pack_size: '', rate: '', current_stock: '' });
      fetchLubes();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return alert('Only admins can delete products.');
    if (!window.confirm('Are you sure you want to delete this Lube item?')) return;
    try {
      await fetch(`/api/lubes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      fetchLubes();
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    try {
      await fetch(`/api/lubes/${editingLube.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(editingLube)
      });
      setEditingLube(null);
      fetchLubes();
    } catch (err) {
      console.error(err);
    }
  };

  const fetchReceipts = async () => {
    try {
      const res = await fetch('/api/lubes/receipts', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await res.json();
      setReceipts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    try {
      await fetch('/api/lubes/receipts', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ ...newReceipt, lube_id: addingStockLube.id })
      });
      setAddingStockLube(null);
      setNewReceipt({ quantity: '', invoice_number: '', business_date: new Date().toISOString().split('T')[0] });
      fetchLubes();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteReceipt = async (id) => {
    if (!isAdmin) return alert('Only admins can delete receipts.');
    if (!window.confirm('Are you sure you want to delete this stock entry? The stock will be automatically deducted.')) return;
    try {
      await fetch(`/api/lubes/receipts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      fetchReceipts();
      fetchLubes();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-amber-500 rounded-3xl p-8 text-white shadow-lg">
        <h1 className="text-3xl font-black mb-2 flex items-center gap-3">
          <Droplet size={32} />
          Oil & Lube Inventory
        </h1>
        <p className="text-amber-100 font-medium text-lg">Manage your master list of lubricants, pack sizes, and prices.</p>
      </div>

      <div className={`grid grid-cols-1 ${isAdmin ? 'lg:grid-cols-3' : ''} gap-8`}>
        
        {/* Add Lube Form */}
        {isAdmin && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden h-fit">
            <div className="p-5 border-b border-slate-100 bg-slate-50">
              <h2 className="font-bold text-slate-800 flex items-center gap-2">
                <Plus className="text-amber-500" size={20} />
                Add New Lube
              </h2>
            </div>
            <div className="p-6">
              <form onSubmit={handleAddLube} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Product Name</label>
                  <input required placeholder="e.g. 4T EXTRA" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" value={newLube.name} onChange={e => setNewLube({...newLube, name: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Pack Size</label>
                  <input required placeholder="e.g. 1L, 500ML" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" value={newLube.pack_size} onChange={e => setNewLube({...newLube, pack_size: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Rate (Price per pack)</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-3.5 text-slate-400" size={18} />
                    <input required type="number" step="0.01" placeholder="0.00" className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-mono" value={newLube.rate} onChange={e => setNewLube({...newLube, rate: e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Initial Stock (Optional)</label>
                  <div className="relative">
                    <Archive className="absolute left-3 top-3.5 text-slate-400" size={18} />
                    <input type="number" placeholder="0" className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-mono" value={newLube.current_stock} onChange={e => setNewLube({...newLube, current_stock: e.target.value})} />
                  </div>
                </div>
                <button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-xl transition-colors shadow-md shadow-amber-500/20 mt-4">
                  Save Product
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Lube List */}
        <div className={`${isAdmin ? 'lg:col-span-2' : ''} bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden`}>
          <div className="p-5 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <Package className="text-amber-500" size={20} />
              Master Lube List
            </h2>
            <div className="flex items-center gap-4">
              <button onClick={() => { fetchReceipts(); setIsHistoryOpen(true); }} className="flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg transition-colors">
                <History size={16} /> Stock History
              </button>
              <span className="bg-amber-100 text-amber-700 font-bold px-3 py-1 rounded-lg text-sm">{lubes.length} items</span>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="p-8 text-center text-slate-500 font-medium">Loading inventory...</div>
            ) : lubes.length === 0 ? (
              <div className="p-12 text-center">
                <Archive size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-bold text-slate-700 mb-2">No Lubes Found</h3>
                <p className="text-slate-500">Add your first oil/lube product using the form on the left.</p>
              </div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead className="bg-white text-slate-500 font-semibold border-b border-slate-100">
                  <tr>
                    <th className="p-4">Item Name</th>
                    <th className="p-4">Pack Size</th>
                    <th className="p-4">Rate (₹)</th>
                    <th className="p-4">Current Stock</th>
                    {isAdmin && <th className="p-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lubes.map((lube) => (
                    <tr key={lube.id} className="hover:bg-slate-50 transition-colors">
                      <td className="p-4 font-bold text-slate-800">{lube.name}</td>
                      <td className="p-4 font-semibold text-slate-600 bg-slate-50/50">{lube.pack_size}</td>
                      <td className="p-4 font-mono font-bold text-emerald-600">₹{lube.rate}</td>
                      <td className="p-4 font-mono font-bold text-slate-700 bg-slate-50/50">{lube.current_stock}</td>
                      {isAdmin && (
                        <td className="p-4 text-right whitespace-nowrap">
                          <button title="Add Stock" onClick={() => setAddingStockLube(lube)} className="p-2 text-emerald-500 hover:bg-emerald-50 rounded-lg transition-colors mr-2">
                            <PlusSquare size={18} />
                          </button>
                          <button title="Edit" onClick={() => setEditingLube(lube)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors mr-2">
                            <Edit2 size={18} />
                          </button>
                          <button title="Delete" onClick={() => handleDelete(lube.id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 size={18} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingLube && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-6">
              <Edit2 className="text-blue-500" size={24} />
              Edit Product
            </h2>
            <form onSubmit={handleEditSave} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Product Name</label>
                <input required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={editingLube.name} onChange={e => setEditingLube({...editingLube, name: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Pack Size</label>
                <input required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none" value={editingLube.pack_size} onChange={e => setEditingLube({...editingLube, pack_size: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Rate (₹)</label>
                <input required type="number" step="0.01" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none font-mono" value={editingLube.rate} onChange={e => setEditingLube({...editingLube, rate: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Current Stock</label>
                <input required type="number" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none font-mono" value={editingLube.current_stock} onChange={e => setEditingLube({...editingLube, current_stock: e.target.value})} />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setEditingLube(null)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 rounded-xl transition-colors">Cancel</button>
                <button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 rounded-xl transition-colors shadow-md shadow-blue-500/20">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Stock Modal */}
      {addingStockLube && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-2">
              <PlusSquare className="text-emerald-500" size={24} />
              Add Stock
            </h2>
            <p className="text-sm text-slate-500 mb-6">Receiving stock for <strong>{addingStockLube.name}</strong></p>
            <form onSubmit={handleAddStock} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Date Received</label>
                <input type="date" required className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 outline-none" value={newReceipt.business_date} onChange={e => setNewReceipt({...newReceipt, business_date: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Quantity Received</label>
                <input required type="number" min="1" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 outline-none font-mono" value={newReceipt.quantity} onChange={e => setNewReceipt({...newReceipt, quantity: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Invoice Number (Optional)</label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-emerald-500 outline-none" value={newReceipt.invoice_number} onChange={e => setNewReceipt({...newReceipt, invoice_number: e.target.value})} />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setAddingStockLube(null)} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 rounded-xl transition-colors">Cancel</button>
                <button type="submit" className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 rounded-xl transition-colors shadow-md shadow-emerald-500/20">Add to Stock</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stock History Modal */}
      {isHistoryOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[80vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <History className="text-blue-500" size={24} />
                Lube Stock Receipts History
              </h2>
              <button onClick={() => setIsHistoryOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors font-bold text-xl">&times;</button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-0">
              {receipts.length === 0 ? (
                <div className="p-12 text-center text-slate-500">No stock history found.</div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-slate-500 font-semibold sticky top-0">
                    <tr>
                      <th className="p-4 border-b border-slate-200">Date</th>
                      <th className="p-4 border-b border-slate-200">Product</th>
                      <th className="p-4 border-b border-slate-200">Quantity Added</th>
                      <th className="p-4 border-b border-slate-200">Invoice No.</th>
                      {isAdmin && <th className="p-4 border-b border-slate-200 text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {receipts.map((r, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="p-4 font-semibold text-slate-700">{new Date(r.business_date).toLocaleDateString('en-IN')}</td>
                        <td className="p-4 font-bold text-slate-800">{r.lube_name} <span className="text-xs text-slate-400 font-normal ml-1">({r.pack_size})</span></td>
                        <td className="p-4 font-mono font-bold text-emerald-600">+{r.quantity}</td>
                        <td className="p-4 text-slate-500">{r.invoice_number || '-'}</td>
                        {isAdmin && (
                          <td className="p-4 text-right">
                            <button title="Delete Receipt" onClick={() => handleDeleteReceipt(r.id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LubeConfig;
